<script setup lang="ts">
import { onMounted } from 'vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseBadge from '@/components/ui/BaseBadge.vue'
import { useDashboardAiSummary } from '@/composables/useDashboardAiSummary'

dayjs.extend(relativeTime)

const { state, refresh } = useDashboardAiSummary()

onMounted(() => {
  void refresh()
})
</script>

<template>
  <!-- AI card: teal left-border is the sole permitted use of this pattern (CLAUDE.md AI badge rule) -->
  <BaseCard
    padding="md"
    class="border-l-4 border-l-teal-500"
    data-testid="ai-summary-card"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <BaseBadge variant="ai" :no-dot="false">AI SUMMARY</BaseBadge>
        <!-- Updating spinner — shown only when a refetch is in flight and prior data exists -->
        <span
          v-if="state.status === 'success' && state.isUpdating"
          class="text-xs text-gray-400 flex items-center gap-1"
          aria-live="polite"
          aria-label="AI summary is updating"
        >
          <svg class="animate-spin h-3 w-3 text-teal-500" viewBox="0 0 24 24" fill="none">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          updating…
        </span>
      </div>
    </template>

    <!-- Loading skeleton -->
    <div v-if="state.status === 'loading'" aria-busy="true" aria-label="Loading AI summary">
      <div class="space-y-2">
        <div class="h-4 bg-gray-200 rounded animate-pulse w-[90%]" />
        <div class="h-4 bg-gray-200 rounded animate-pulse w-[80%]" />
        <div class="h-4 bg-gray-200 rounded animate-pulse w-[60%]" />
      </div>
    </div>

    <!-- Success state -->
    <template v-else-if="state.status === 'success'">
      <p class="text-sm text-gray-800 leading-relaxed">{{ state.data.summary }}</p>
      <p
        class="text-xs text-gray-500 mt-2"
        :title="state.data.generatedAt ?? undefined"
      >
        Generated {{ state.data.generatedAt ? dayjs(state.data.generatedAt).fromNow() : 'just now' }}
        <span v-if="state.data.cached" class="ml-1 text-gray-400">(cached)</span>
      </p>
    </template>

    <!-- Error / rate-limited state -->
    <template v-else-if="state.status === 'error'">
      <p class="text-sm text-gray-500">{{ state.message }}</p>
      <button
        v-if="!state.retryAfterSeconds"
        class="mt-2 text-xs text-teal-600 underline hover:text-teal-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
        @click="void refresh()"
      >
        Try again
      </button>
    </template>

    <!-- Idle (pre-mount, invisible in practice) -->
    <template v-else>
      <div class="h-4 bg-gray-100 rounded w-[90%]" />
    </template>
  </BaseCard>
</template>
